// Ways into the evening planning from Vandaag (AV1 AC4, AC6) and klaarleggen in its own block (AV1 step 4).
import { useState } from "react";
import { ArrowRight, ChevronDown, X } from "lucide-react";
import { Button, cn } from "../components/ui";
import type { DayBlock } from "../lib/db";
import { setBlockStatus } from "../lib/days";
import { useChecklist, useSession } from "../lib/evening";
import { navigate, useCollapsed } from "../lib/hooks";
import { addDays } from "@shared/time";
import { getMeta, setMeta } from "../lib/db";
import { LayOut } from "./Evening";
import { useLiveQuery } from "dexie-react-hooks";

/** In the block "Volgende dag plannen": start (or continue) tomorrow, or say it is ready. */
export function EveningStart({ today }: { today: string }) {
  const session = useSession(addDays(today, 1), "evening");
  if (session === undefined) return null;
  if (session?.finished_at) {
    return (
      <p className="mb-1 ml-10 text-sm font-semibold text-pine">
        Morgen staat klaar.{" "}
        <a
          href="/avond"
          onClick={(e) => {
            e.preventDefault();
            navigate("/avond");
          }}
          className="underline"
        >
          Nog eens bekijken
        </a>
      </p>
    );
  }
  // Under the step, in line with its title: an action of this step, not a big block between the steps.
  return (
    <button
      type="button"
      onClick={() => navigate("/avond")}
      className="mb-1 ml-10 inline-flex min-h-11 items-center gap-1.5 rounded-xl border-2 border-pine px-3 font-semibold text-pine"
    >
      {session ? "Verder met morgen klaarzetten" : "Morgen klaarzetten"}
      <ArrowRight aria-hidden className="size-4" />
    </button>
  );
}

/**
 * In the block "Klaarleggen": the same checklist as step 4 of the evening planning, for tomorrow. It folds in and
 * out, and the round of the block goes on by itself when everything is laid out (B32).
 */
export function LayOutInBlock({ today, block }: { today: string; block?: DayBlock }) {
  const tomorrow = addDays(today, 1);
  const rows = useChecklist(tomorrow);
  const [folded, toggle] = useCollapsed(`prep:${tomorrow}`);
  const done = rows?.filter((r) => r.checkId).length ?? 0;
  const all = !!rows?.length && done === rows.length;
  if (!rows) return null;
  return (
    <div className="mb-1 ml-10">
      {rows.length > 0 && (
        <button
          type="button"
          aria-expanded={!folded}
          onClick={toggle}
          className={cn("-ml-2 flex min-h-11 items-center gap-1 rounded-xl px-2 text-sm", all ? "font-semibold text-pine" : "text-ink-soft")}
        >
          <span className="tabular">{`${done} van ${rows.length} klaargelegd`}</span>
          <ChevronDown aria-hidden className={cn("size-4 transition-transform", !folded && "rotate-180")} />
          <span className="sr-only">{folded ? "Klaarleggen tonen" : "Klaarleggen verbergen"}</span>
        </button>
      )}
      {!folded && (
        <LayOut
          date={tomorrow}
          onAllChecked={() => {
            if (block && block.status === "open") void setBlockStatus(block, "done");
          }}
        />
      )}
    </div>
  );
}

/**
 * "Snel plannen" (AV1 AC6): the morning after an evening that was skipped, a small button for steps 2 and 3
 * of today. Until noon, and gone once done or closed.
 */
export function QuickPlan({ today, minute }: { today: string; minute: number }) {
  const evening = useSession(today, "evening");
  const quick = useSession(today, "quick");
  // null: never closed (undefined is still loading).
  const dismissed = useLiveQuery(async () => (await getMeta<string>("quickPlanClosed")) ?? null, []);
  const [closed, setClosed] = useState(false);
  if (evening === undefined || quick === undefined || dismissed === undefined) return null;
  if (closed || dismissed === today || minute >= 12 * 60 || evening?.finished_at || quick?.finished_at) return null;
  return (
    <div className="mb-5 flex items-center gap-2 rounded-2xl border border-mist bg-surface p-2 pl-3">
      <p className="min-w-0 flex-1 text-ink-soft">Gisteravond niet gepland.</p>
      <Button variant="secondary" onClick={() => navigate("/avond/snel")}>
        Snel plannen
      </Button>
      <button
        type="button"
        aria-label="Snel plannen sluiten"
        onClick={() => {
          setClosed(true);
          void setMeta("quickPlanClosed", today);
        }}
        className="flex size-11 shrink-0 items-center justify-center rounded-xl text-ink-soft"
      >
        <X aria-hidden className="size-5" />
      </button>
    </div>
  );
}
